
import Vue from "vue"
import axios, { AxiosError, AxiosResponse } from "axios"
import { EventBus } from "@/plugins/eventBus"
import StatusSelect from "@/components/StatusSelect.vue"
import { VueConstructor } from "vue/types/umd"
import DateFormatMixin from "@/mixins/DateFormatMixin.vue"
import DatePicker from "@/components/DatePicker.vue"
import MobilePhoneNumberInput from "../MobilePhoneNumberInput.vue"

interface Profile {
    [key: string]: string
}

interface SelectElt {
  value: string,
  text: string
}

interface Body {
    [key: string]: string
}

interface EmergencyContact {
    id: number,
    name: string,
    phone: string,
    created_at: string,
    updated_at: string
}

export default (Vue as VueConstructor<Vue & InstanceType<typeof DateFormatMixin> & InstanceType<typeof MobilePhoneNumberInput>>).extend({
    name: "WorkerFormUpdateProfile",
    mixins: [
        DateFormatMixin,
    ],
    components: { StatusSelect, DatePicker, MobilePhoneNumberInput },
    props: {
        id: {
            type: Number,
            required: true
        }
    },
    data: () => ({
        dataReference: {} as Profile,
        profile: {
            id: "",
            space_name: "",
            profile_picture: "",
            sex: "",
            first_name: "",
            last_name: "",
            maiden_name: "",
            status: "",
            state: "",
            email: "",
            agency_id: "",
            mobile_phone_number: "",
            phone_number: "",
            postal_address: "",
            postal_complement: "",
            postal_code: "",
            postal_city: "",
            postal_country: "",
            nationality: "",
            birth_date: "",
            birth_city: "",
            birth_department: "",
            birth_country: "",
            nir: "",
            family_situation: "",
            child_in_charge: "",
            wage_category: "",
            payement_category: "",
            bank_domiciliation: "",
            bank_beneficiary: "",
            bank_code: "",
            bank_wicket_code: "",
            bank_account_number: "",
            bank_key: "",
            bank_country_code: "",
            bank_control_key: "",
            bank_bic: "",
            created_at: "",
            updated_at: "",
            external_ref: "",
            transportation_means: ""
        } as Profile,
        stateList: [] as Array<SelectElt>,
        civilityList: [] as Array<SelectElt>,
        transportList: [] as Array<SelectElt>,
        wageCategoryList: [] as Array<SelectElt>,
        payementCategoryList: [] as Array<SelectElt>,
        familySituationList: [] as Array<SelectElt>,
        comparison: {} as Body,
        emailRegex: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
        maxBirthDate: "",
        emergencyContacts: [] as Array<EmergencyContact>,
        formattedIBAN: "",
        newEmergencyContact: {
            name: "",
            phone: ""
        } as EmergencyContact
    }),
    created: function() {
        let date = new Date()
        date.setFullYear(date.getFullYear() - 18)
        this.maxBirthDate = date.toISOString().substr(0, 10)
    },
    computed: {
        isEmergencyContactValid() {
            const name = this.newEmergencyContact.name;
            const phone = this.newEmergencyContact.phone;
            const nameValid = name !== '' && (this.contactLengthRule as (v: string) => true | string)(name) === true;
            const phoneValid = phone !== '' && (this.emergencyContactPhoneNumberRule as (v: string) => true | string)(phone) === true;
            return nameValid && phoneValid;
        },
        isEmergencyContactUpdateValid() {
            return (emergencyContact: EmergencyContact) => {
                const name = emergencyContact.name.trim();
                const phone = emergencyContact.phone.trim();
                
                if (name === '' || phone === '') {
                    return false;
                }
                
                const nameValid1 = (this.contactLengthRule as (v: string) => true | string)(name) === true;
                const nameValid2 = (this.contactNameRule as (v:string)=> true | string)(name) === true;
                const phoneValid = (this.emergencyContactPhoneNumberRule as (v: string) => true | string)(phone) === true;
                
                return nameValid1 && nameValid2 && phoneValid;
            };
        },
        emailRule() {
            return (v: string) => (this.emailRegex.test(v) || !v) || this.$vuetify.lang.t('$vuetify.invalid_email')
        },
        emailOrPhoneRequired() {
            return (v: string) => (!!this.profile.email  || !!this.profile.mobile_phone_number) || this.$vuetify.lang.t('$vuetify.phone_or_email_required')
        },
        mobilePhoneNumberRule() {
            return (v: string) => ((v.length > 1 && v.length < 15) || !v) || this.$vuetify.lang.t('$vuetify.invalid_length')
        },
        nameLengthRule() {
            return (v: string) => ((v.length > 1 && v.length < 100) || !v) || this.$vuetify.lang.t('$vuetify.invalid_length')
        },
        contactLengthRule() {
            return (v: string) => ((v.length > 2 && v.length < 100) || !v) || this.$vuetify.lang.t('$vuetify.invalid_length')
        },
        contactNameRule() {
            return (v: string) => (!/^\d+$/.test(v) || !v) || this.$vuetify.lang.t('$vuetify.invalid_name_number')
        },
        emergencyContactPhoneNumberRule() {
            return (v: string) =>
                (
                    (
                        (/^0[1-9]\d{8}$/.test(v)
                        || /^\+33[1-9]\d{8}$/.test(v)
                        || /^0033[1-9]\d{8}$/.test(v))
                        && ( v.length < 15 ) || !v
                    )
                )
                || this.$vuetify.lang.t('$vuetify.invalid_phone_format')
        },
        stringLengthRule() {
            return (v: string) => ((v.length > 1 && v.length < 254) || !v) || this.$vuetify.lang.t('$vuetify.invalid_length')
        }
    },
    methods: {
        async deleteEmergencyContact(emergencyContactId: number) {
            let snackbar = {}
            await axios
                .delete(`/v1/workers/${this.id}/emergency_contacts/${emergencyContactId}`)
                .then(() => {
                    snackbar = {
                        message: this.$vuetify.lang.t('$vuetify.delete_emergency_contact_success'),
                        color: 'success'
                    }
                })
                .catch((error: AxiosError) => {
                    snackbar = {
                        message: this.$vuetify.lang.t('$vuetify.delete_emergency_contact_error'),
                        color: 'error'
                    }
                }).finally(() => {
                    this.getEmergencyContacts()
                    EventBus.$emit('snackbar',snackbar);
                })
        },
        async editEmergencyContact(emergencyContact: EmergencyContact) {
            let snackbar = {}
            await axios
                .put(`/v1/workers/${this.id}/emergency_contacts/${emergencyContact.id}`,{
                    name: emergencyContact.name,
                    phone: emergencyContact.phone
                })
                .then((response: AxiosResponse) => {
                    snackbar = {
                        message: this.$vuetify.lang.t('$vuetify.update_emergency_contact_success'),
                        color: 'success'
                    }
                })
                .catch((error: AxiosError) => {
                    snackbar = {
                        message: this.errorMessageBuilder(error.response?.data),
                        color: 'error'
                    }
                }).finally(() => {
                    EventBus.$emit('snackbar', snackbar);
                    this.getEmergencyContacts()
                })
        },
        errorMessageBuilder(dataErrorResponse: string) {
            let errorMessage =""
            switch (dataErrorResponse) {
                case "This phone number is already used for this worker":
                    errorMessage = this.$vuetify.lang.t('$vuetify.add_emergency_contact_error_phone_already_used')
                    break
                case "Maximum number of emergency contacts (3) reached for this worker":
                    errorMessage = this.$vuetify.lang.t('$vuetify.add_emergency_contact_error_maximum_number_reached')
                    break
                default:
                    errorMessage = this.$vuetify.lang.t('$vuetify.add_emergency_contact_error')
            }
            return errorMessage
        },
        updateBirthDate(birth_date: string): void{
            this.profile.birth_date = birth_date
        },
        async getWorker() {
            await axios
                .get(`/v1/workers/${this.id}?fields=${Object.keys(this.profile).toString()}`)
                .then((response: AxiosResponse) => {
                    for (const property in response.data) {
                        this.profile[property as keyof Profile] = response.data[property] ?? ""
                        this.dataReference[property as keyof Profile] = response.data[property] ?? ""
                    }
                    this.uppercase()
                    this.updateFormattedIBAN()
                })
                .catch((error: AxiosError) => {
                    EventBus.$emit('snackbar',{axiosError: error});
                })
            this.comparison = {}
        },
        getSpace() {
            axios
                .get(`/v1/spaces/${this.profile.agency_id}`)
                .then((response: AxiosResponse) => {
                    this.profile.space_name = response.data[0].name
                })
                .catch((error: AxiosError) => {
                    EventBus.$emit('snackbar',{axiosError: error});
                })
            this.comparison = {}
        },
        async getEmergencyContacts() {
           await axios
                .get(`/v1/workers/${this.id}/emergency_contacts`)
                .then((response: AxiosResponse) => {
                    this.emergencyContacts = response.data
                })
                .catch((error: AxiosError) => {
                    EventBus.$emit('snackbar',{axiosError: error});
                })
        },
        addEmergencyContact() {
            let snackbar = {
                message: this.$vuetify.lang.t('$vuetify.add_emergency_contact_success'),
                color: 'success'
            }
             axios
                .post(`/v1/workers/${this.id}/emergency_contacts`, this.newEmergencyContact)
                .then(() => {
                    this.getEmergencyContacts()
                    this.newEmergencyContact = {
                        name: "",
                        phone: ""
                    } as EmergencyContact
                    this.$nextTick(() => {
                        if (this.$refs.form) {
                            (this.$refs.form as Vue & { resetValidation: () => void }).resetValidation()
                        }
                    })
                })
                .catch((error: AxiosError) => {
                    snackbar = {
                        message: this.errorMessageBuilder(error.response?.data),
                        color: 'error'
                    }
                }).finally(() => {
                    EventBus.$emit('snackbar', snackbar);
                })
        },
        async getWorkerDocument() {
            await axios
                .get(`/v1/workers/${this.id}/documents`)
                .then((response: AxiosResponse) => {
                    response.data.forEach((document: {type: string}) => {
                        if (document.type === "rib" && this.profile.payement_category === "") {
                            this.profile.payement_category = "bank_transfer"
                        }
                    });
                })
                .catch((error: AxiosError) => {
                    EventBus.$emit('snackbar',{axiosError: error});
                })
        },
        uppercase() {
            this.profile.last_name = this.profile.last_name.toUpperCase();
        },
        updateData() {
            if (Object.keys(this.comparison).length > 0 && (this.$refs.form as Vue & { validate: () => boolean }).validate()) {
                const updatePromises = Object.keys(this.comparison).map(key => {
                    const workerData = { [key]: this.comparison[key] }
                    return axios.put(`/v1/workers/${this.id}`, workerData)
                })

                Promise.all(updatePromises)
                    .then(() => {
                        EventBus.$emit('snackbar', {message: this.$vuetify.lang.t('$vuetify.update_success')})
                        this.getWorker()
                        this.getWorkerDocument()
                    })
                    .catch((e: AxiosError) => {
                        EventBus.$emit('snackbar', {axiosError: e})
                    })
            }
        },
        cancel() {
            if (Object.keys(this.comparison).length > 0 ) {
                this.getWorker()
            }
        },
        updateDocument(event: any) {
            let formData = new FormData()
            formData.append("files[]", event.target.files[0])

            axios
                .post("/uploads", formData)
                .then((response) => {
                    this.profile.profile_picture = response.data.success[0]
                    
                    this.comparison["profile_picture"] = response.data.success[0]
                    this.updateData()
                })
        },
        formatIBAN() {
            const parts = [
                this.profile.bank_country_code,
                this.profile.bank_control_key,
                this.profile.bank_code,
                this.profile.bank_wicket_code,
                this.profile.bank_account_number,
                this.profile.bank_key
            ].filter(part => part && part.trim() !== '')
            if (parts.length === 0) {
                return this.$vuetify.lang.t('$vuetify.iban_incomplete')
            }
            const iban = parts.join('').toUpperCase()            
            return iban.replace(/(.{4})/g, '$1 ').trim()
        },
        handleIBANInput(value: string) {
            const cleanIBAN = value.replace(/[^A-Za-z0-9]/g, '').toUpperCase()
            const limitedIBAN = cleanIBAN.substring(0, 27)
            this.parseIBANToFields(limitedIBAN)
            this.formattedIBAN = this.formatIBANDisplay(limitedIBAN)
        },
        parseIBANToFields(iban: string) {
            this.profile.bank_country_code = iban.substring(0, 2) || ''
            this.profile.bank_control_key = iban.substring(2, 4) || ''
            this.profile.bank_code = iban.substring(4, 9) || ''
            this.profile.bank_wicket_code = iban.substring(9, 14) || ''
            this.profile.bank_account_number = iban.substring(14, 25) || ''
            this.profile.bank_key = iban.substring(25, 27) || ''
        },
        formatIBANDisplay(iban: string) {
            return iban.replace(/(.{4})/g, '$1 ').trim()
        },
        updateFormattedIBAN() {
            const parts = [
                this.profile.bank_country_code,
                this.profile.bank_control_key,
                this.profile.bank_code,
                this.profile.bank_wicket_code,
                this.profile.bank_account_number,
                this.profile.bank_key
            ].join('')
            
            this.formattedIBAN = this.formatIBANDisplay(parts)
        },
        formatIBANOnBlur() {
            const cleanIBAN = this.formattedIBAN.replace(/[^A-Za-z0-9]/g, '').toUpperCase()
            this.formattedIBAN = this.formatIBANDisplay(cleanIBAN)
        }
    },
    async mounted() {
        await this.getWorker()
        this.getSpace()
        await this.getEmergencyContacts()
        this.stateList = [
            {value: "web", text: this.$vuetify.lang.t("$vuetify.worker_states.web").toUpperCase()},
            {value: "candidate", text: this.$vuetify.lang.t("$vuetify.worker_states.candidate").toUpperCase()},
            {value: "employee", text: this.$vuetify.lang.t("$vuetify.worker_states.employee").toUpperCase()},
            {value: "applicant", text: this.$vuetify.lang.t("$vuetify.worker_states.applicant").toUpperCase()},
            {value: "pre-registered", text: this.$vuetify.lang.t("$vuetify.worker_states.pre_registered").toUpperCase()},
            {value: "supplements OK", text: this.$vuetify.lang.t("$vuetify.worker_states.supplement_ok").toUpperCase()},
        ]
        this.transportList = [
            {value: "car", text: this.$vuetify.lang.t("$vuetify.car")},
            {value: "motorbike", text: this.$vuetify.lang.t("$vuetify.motorbike")},
            {value: "bike_scooter", text: this.$vuetify.lang.t("$vuetify.bike_scooter")},
            {value: "mass_transit", text: this.$vuetify.lang.t("$vuetify.mass_transit")},
            {value: "moped", text: this.$vuetify.lang.t("$vuetify.moped")},
            {value: "walk", text: this.$vuetify.lang.t("$vuetify.walk")},
            {value: "carpool", text: this.$vuetify.lang.t("$vuetify.carpool")}
        ]
        this.wageCategoryList = [
            {value: "executive", text: this.$vuetify.lang.t("$vuetify.executive")},
            {value: "short_haul_driver", text: this.$vuetify.lang.t("$vuetify.short_haul_drivers")},
            {value: "teacher_contract_45", text: this.$vuetify.lang.t("$vuetify.teacher_contract_45")},
            {value: "employee", text: this.$vuetify.lang.t("$vuetify.employee")},
            {value: "EEC_employee", text: this.$vuetify.lang.t("$vuetify.EEC_employee")},
            {value: "long_haul_driver", text: this.$vuetify.lang.t("$vuetify.long_haul_driver")},
            {value: "executive_pension", text: this.$vuetify.lang.t("$vuetify.executive_pension")},
            {value: "executive_pension_65", text: this.$vuetify.lang.t("$vuetify.executive_pension_65")},
            {value: "employee_pension", text: this.$vuetify.lang.t("$vuetify.employee_pension")},
            {value: "employee_pension_65", text: this.$vuetify.lang.t("$vuetify.employee_pension_65")}
        ]
        this.payementCategoryList = [
            {value: "bank_transfer", text: this.$vuetify.lang.t("$vuetify.bank_transfer")},
            {value: "bank_cheque", text: this.$vuetify.lang.t("$vuetify.bank_cheque")}
        ]
        this.familySituationList = [
            {value: "single", text: this.$vuetify.lang.t("$vuetify.single")},
            {value: "in_couple", text: this.$vuetify.lang.t("$vuetify.in_couple")},
            {value: "married", text: this.$vuetify.lang.t("$vuetify.married")},
            {value: "divorced", text: this.$vuetify.lang.t("$vuetify.divorced")},
            {value: "widowed", text: this.$vuetify.lang.t("$vuetify.widowed")},
            {value: "civil_union", text: this.$vuetify.lang.t("$vuetify.civil_union")},
            {value: "separated", text: this.$vuetify.lang.t("$vuetify.separated")}
        ]
        this.civilityList = [
            {value: "M", text: this.$vuetify.lang.t("$vuetify.mister").toUpperCase()},
            {value: "F", text: this.$vuetify.lang.t("$vuetify.misses").toUpperCase()}
        ]
        await this.getWorkerDocument()
    },
    watch: {
        async $route () {
            await this.getWorker()
            await this.getEmergencyContacts()
            this.getSpace()
        },
        profile: {
            handler(){
                for(const property in this.dataReference) {
                    if(this.profile[property] !== this.dataReference[property]) {
                        this.comparison[property] = this.profile[property];
                    } else {
                        delete this.comparison[property]
                    }
                }
                this.updateFormattedIBAN()
            },
            deep: true
        }
    },
})
